import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  DirectionsRenderer,
} from "@react-google-maps/api";
import {
  getStoredData,
  saveToGlobalState,
  removeSpecificData,
} from "../../utils/localStorage";
import StepFooter from "../../components/step-footer/StepFooter";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const containerStyle = { width: "100%", height: "400px" };
const defaultCenter = { lat: -15.7801, lng: -47.9292 };
const mapId = "DEMO_MAP_ID";

const ROUTE_SELECTED_STORAGE_KEY = "rota_selecionada";
const ROUTE_HOURS_STORAGE_KEY = "horas_rota_selecionada";

function createMarkerContent(label) {
  const el = document.createElement("div");
  el.textContent = label;
  el.style.width = "28px";
  el.style.height = "28px";
  el.style.borderRadius = "999px";
  el.style.display = "flex";
  el.style.alignItems = "center";
  el.style.justifyContent = "center";
  el.style.background = "#1e88e5";
  el.style.color = "#fff";
  el.style.fontSize = "14px";
  el.style.fontWeight = "700";
  el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.25)";
  el.style.border = "2px solid #fff";
  return el;
}

function buildStoredStop(stored, prefix) {
  const name = stored[`cidade_${prefix}`] || "";
  const mainCity = stored[`cidade_${prefix}_main`] || "";
  const searchValue = stored[`search_${prefix}`] || "";

  const lat = Number(stored[`lat_cidade_${prefix}`]);
  const lng = Number(stored[`lng_cidade_${prefix}`]);

  const placeId =
    stored[`place_id_${prefix}`] || stored[`station_${prefix}_place_id`] || "";

  if (!name || Number.isNaN(lat) || Number.isNaN(lng)) return null;

  return {
    place_id: placeId,
    name,
    mainCity,
    searchValue,
    position: { lat, lng },
  };
}

function formatDurationPt(seconds = 0) {
  const totalMinutes = Math.max(0, Math.round(Number(seconds) / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours} hora${hours === 1 ? "" : "s"}`);
  if (minutes > 0 || hours === 0) {
    parts.push(`${minutes} minuto${minutes === 1 ? "" : "s"}`);
  }

  return parts.join(" ");
}

function getRouteTotalDurationSeconds(route) {
  const legs = route?.legs || [];
  return legs.reduce(
    (acc, leg) => acc + (Number(leg?.duration?.value) || 0),
    0,
  );
}

function getRouteSummary(route, index) {
  const summary = String(route?.summary || "").trim();
  if (summary) return summary;
  return `Rota ${index + 1}`;
}

function persistSelectedRoute(routeItem) {
  if (!routeItem) return;

  const routeKey = `opcao-rota-${routeItem.index}`;
  const hoursValue = Number((routeItem.durationSeconds / 3600).toFixed(2));

  saveToGlobalState({
    [ROUTE_SELECTED_STORAGE_KEY]: routeKey,
    [ROUTE_HOURS_STORAGE_KEY]: hoursValue,
  });
}

function Trajeto() {
  const navigate = useNavigate();

  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 935);

  const [directions, setDirections] = useState(null);
  const [originPos, setOriginPos] = useState(null);
  const [destinationPos, setDestinationPos] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  const [routeOptions, setRouteOptions] = useState([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [loadingRoutes, setLoadingRoutes] = useState(false);

  const mapRef = useRef(null);
  const hasRestoredRef = useRef(false);
  const originMarkerRef = useRef(null);
  const destinationMarkerRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_API_KEY,
    language: "pt-BR",
    region: "BR",
  });

  useEffect(() => {
    if (!isLoaded || !window.google || hasRestoredRef.current) return;

    const stored = getStoredData();

    const restoredOrigin = buildStoredStop(stored, "origem");
    const restoredDestination = buildStoredStop(stored, "destino");

    if (restoredOrigin) setOriginPos(restoredOrigin.position);
    if (restoredDestination) setDestinationPos(restoredDestination.position);

    if (restoredDestination?.position) {
      setMapCenter(restoredDestination.position);
    } else if (restoredOrigin?.position) {
      setMapCenter(restoredOrigin.position);
    }

    hasRestoredRef.current = true;
  }, [isLoaded]);

  useEffect(() => {
    if (!originPos || !destinationPos || !window.google) return;

    const DirectionsService = new window.google.maps.DirectionsService();

    setLoadingRoutes(true);

    DirectionsService.route(
      {
        origin: originPos,
        destination: destinationPos,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: true,
      },
      (result, status) => {
        setLoadingRoutes(false);

        if (status === "OK" && result?.routes?.length) {
          setDirections(result);
        } else {
          console.error("Erro ao gerar rota:", status);
          setDirections(null);
          setRouteOptions([]);
        }
      },
    );
  }, [originPos, destinationPos]);

  useEffect(() => {
    if (!directions?.routes?.length) {
      setRouteOptions([]);
      return;
    }

    const options = directions.routes.map((route, index) => {
      const durationSeconds = getRouteTotalDurationSeconds(route);
      const durationText = formatDurationPt(durationSeconds);
      const summary = getRouteSummary(route, index);

      return {
        key: `opcao-rota-${index}`,
        index,
        summary,
        durationSeconds,
        durationText,
        rawRoute: route,
      };
    });

    setRouteOptions(options);

    let initialIndex = 0;

    const stored = getStoredData();
    const savedKey = stored?.[ROUTE_SELECTED_STORAGE_KEY];

    if (savedKey) {
      const savedIndex = options.findIndex((item) => item.key === savedKey);
      if (savedIndex >= 0) initialIndex = savedIndex;
    }

    setSelectedRouteIndex(initialIndex);
  }, [directions]);

  useEffect(() => {
    if (!routeOptions.length) return;

    const safeIndex = Math.min(
      Math.max(selectedRouteIndex, 0),
      routeOptions.length - 1,
    );

    const selected = routeOptions[safeIndex];
    if (!selected) return;

    persistSelectedRoute(selected);
  }, [routeOptions, selectedRouteIndex]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    let cancelled = false;

    (async () => {
      const { AdvancedMarkerElement } =
        await window.google.maps.importLibrary("marker");

      if (cancelled) return;

      if (originMarkerRef.current) {
        originMarkerRef.current.map = null;
        originMarkerRef.current = null;
      }

      if (destinationMarkerRef.current) {
        destinationMarkerRef.current.map = null;
        destinationMarkerRef.current = null;
      }

      const map = mapRef.current;

      if (originPos) {
        originMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: originPos,
          title: "Origem",
          content: createMarkerContent("A"),
        });
      }

      if (destinationPos) {
        destinationMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: destinationPos,
          title: "Destino",
          content: createMarkerContent("B"),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, originPos, destinationPos]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    const raf = requestAnimationFrame(() => {
      const map = mapRef.current;
      if (!map) return;

      if (originPos && destinationPos) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(originPos);
        bounds.extend(destinationPos);

        map.fitBounds(bounds, {
          top: 80,
          bottom: 80,
          left: 80,
          right: 80,
        });
        return;
      }

      if (originPos) {
        map.setCenter(originPos);
        map.setZoom(14);
        return;
      }

      if (destinationPos) {
        map.setCenter(destinationPos);
        map.setZoom(14);
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [isLoaded, originPos, destinationPos]);

  const handleSelectRoute = (index) => {
    setSelectedRouteIndex(index);
  };

  const handleNext = () => {
    removeSpecificData("cidades_trajeto");

    saveToGlobalState({
      cidades_trajeto: [],
    });

    navigate("/data");
  };

  const handleBack = () => {
    const stored = getStoredData();
    const type = stored?.qual_meio_transporte;

    const routes = {
      carro: "/carro",
      moto: "/moto",
    };

    const route = routes[type];

    if (route) navigate(route);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    const timer = setTimeout(() => {
      window.google.maps.event.trigger(mapRef.current, "resize");
    }, 150);

    return () => clearTimeout(timer);
  }, [isLoaded, originPos, destinationPos]);

  return (
    <>
      <style>{`
        :root {
          --primary: #0a84ff;
          --primary-dark: #0066cc;
          --bg: #eef4fb;
          --white: #ffffff;
          --border: rgba(148, 163, 184, 0.22);
          --text: #0f172a;
          --text-light: #64748b;
          --accent: #7c3aed;
          --accent-2: #06b6d4;
        }

        html, body, #root {
          height: 100%;
          margin: 0;
          overflow: hidden;
        }

        * {
          box-sizing: border-box;
        }

        .main-container {
          display: flex;
          height: calc(98vh - 144px);
          overflow: hidden;
          padding: 12px;
          gap: 12px;
        }

        .content-side {
          width: 470px;
          min-width: 470px;
          padding: 22px;
          display: flex;
          flex-direction: column;
          border-radius: 28px;
          -webkit-backdrop-filter: blur(18px);
          overflow: hidden;
          position: relative;
        }

        .content-side h1 {
          margin: 0 0 8px 0;
          font-size: 1.35rem;
          line-height: 1.15;
          font-weight: 800;
          color: var(--text);
          letter-spacing: -0.03em;
          position: relative;
          z-index: 1;
        }

        .content-side > div:first-of-type {
          position: relative;
          z-index: 1;
          padding: 12px 14px;
          border-radius: 18px;
          color: var(--text-light);
        }

        .content-side > div:first-of-type p {
          margin: 0;
          font-size: 0.95rem;
          line-height: 1.45;
        }

        .route-list {
          flex: 1;
          margin-top: 16px;
          padding-right: 6px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 12px;
          position: relative;
          z-index: 1;
          background: transparent !important
        }

        .route-list::-webkit-scrollbar {
          width: 10px;
        }

        .route-list::-webkit-scrollbar-track {
          background: transparent;
        }

        .route-list::-webkit-scrollbar-thumb {
          background: rgba(100, 116, 139, 0.26);
          border-radius: 999px;
        }

        .route-card {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          height: 80px;
          min-height: 80px;
          padding: 16px;
          border-radius: 18px;
          border: 1px solid var(--border);
          background: rgba(255, 255, 255, 0.92);
          cursor: pointer;
          transition:
            transform 0.18s ease,
            box-shadow 0.18s ease,
            border-color 0.18s ease,
            background 0.18s ease;
          box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.1);
          position: relative;
          overflow: hidden;
        }

        .route-card::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: linear-gradient(180deg, var(--primary), var(--accent));
          opacity: 0;
          transition: opacity 0.18s ease;
        }

        .route-card:hover {
          transform: translateY(-2px);
          border-color: rgba(10, 132, 255, 0.30);
          box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
        }

        .route-card:hover::before {
          opacity: 1;
        }

        .route-card.selected {
          background: linear-gradient(135deg, rgba(10,132,255,0.10), rgba(124,58,237,0.08));
          border-color: rgba(10, 132, 255, 0.52);
          box-shadow: 0 18px 34px rgba(10, 132, 255, 0.12);
          transform: translateY(-1px);
        }

        .route-card.selected::before {
          opacity: 1;
        }

        .route-card input[type="radio"] {
          width: 18px;
          height: 18px;
          accent-color: var(--primary);
          flex-shrink: 0;
          margin-top: 3px;
          cursor: pointer;
        }

        .route-card-content {
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 0;
        }

        .route-card-title {
          font-size: 0.98rem;
          color: var(--text);
          font-weight: 800;
          line-height: 1.35;
        }

        .route-card-duration {
          font-size: 0.92rem;
          color: var(--text-light);
          font-weight: 600;
          line-height: 1.35;
        }

        .actions-row {
          display: flex;
          gap: 12px;
          padding-top: 16px;
          margin-top: 10px;
          position: relative;
          z-index: 1;
        }

        .btn-next {
            flex: 1;
            padding: 14px 16px;
            border: none;
            color: #fff;
            cursor: pointer;
            transition:
              transform 0.18s ease,
              box-shadow 0.18s ease,
              filter 0.18s ease;
          }

        .btn-back {
          flex: 1;
          padding: 14px 16px;
          border: 1px solid rgba(148, 163, 184, 0.28);
          color: var(--text);
          cursor: pointer;
          transition:
            transform 0.18s ease,
            box-shadow 0.18s ease,
            background 0.18s ease,
            border-color 0.18s ease;
        }

        .map-side {
          flex: 1;
          position: relative;
          min-width: 0;
          border-radius: 28px;
          overflow: hidden;
          background: #dfe9f5;
          border: 1px solid rgba(255, 255, 255, 0.7);
        }

        .map-side > div {
          width: 100%;
          height: 100%;
        }

        @media (max-width: 934px) {
          html,
          body,
          #root {
            height: 100%;
            overflow: hidden !important;
          }

          .main-container {
            display: flex;
            flex-direction: column;
            width: 100%;
            max-width: 100%;
            height: calc(100vh - 100px);
            overflow: hidden;
            padding: 10px;
            gap: 8px;
          }

          .content-side {
            display: contents;
          }

          .content-side h1 {
            order: 1;
            margin: 0 10px 4px;
            font-size: 0.9rem;
            flex-shrink: 0;
          }

          .route-list {
            order: 2;
            margin: 0 10px;
            overflow: hidden;
            flex: 0 0 auto;
            gap: 8px;
          }

          .route-card {
            height: 60px;
            min-height: 60px;
            padding: 10px;
          }

          .route-card-title {
            font-size: 0.85rem;
          }

          .route-card-duration {
            font-size: 0.8rem;
          }

          .map-side {
              width: 100vw;
              max-width: 100%;
              overflow: hidden;
              margin: 0;
              box-sizing: border-box;
            }

            .map-container {
              width: 100% !important;
              max-width: 100% !important;
              overflow: hidden !important;
            }

            .map-side > div {
              width: 100% !important;
              max-width: 100% !important;
            }

            .map-side .gm-style {
              width: 100% !important;
              max-width: 100% !important;
            }

          .actions-row {
            order: 4;
            margin: 0 10px;
            padding-top: 6px;
            flex-shrink: 0;
            display: flex;
            gap: 10px;
          }

          .actions-row button {
            width: 100%;
            padding: 12px;
          }
        }

        @media (min-width: 935px) { 
          .content-side::after {
            content: "";
            position: absolute;
            inset: auto auto -120px -80px;
            width: 240px;
            height: 240px;
            pointer-events: none;
          }

          .content-side::before {
            content: "";
            position: absolute;
            inset: -120px -60px auto auto;
            width: 220px;
            height: 220px;
            pointer-events: none;
          }

          .map-container {
            height: 400px; 
          }
        }
          
      `}</style>

      <div className="main-container">
        <div className="content-side">
          <h1>ROTAS DISPONÍVEIS</h1>

          <div className="route-list">
            {loadingRoutes && <p>Carregando rotas...</p>}

            {!loadingRoutes && routeOptions.length === 0 && (
              <p>Nenhuma rota foi encontrada.</p>
            )}

            {routeOptions.map((routeItem, index) => {
              const isSelected = index === selectedRouteIndex;

              return (
                <label
                  key={routeItem.key}
                  className={`route-card ${isSelected ? "selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="rota-selecionada"
                    checked={isSelected}
                    onChange={() => handleSelectRoute(index)}
                  />
                  <div className="route-card-content">
                    <div className="route-card-title">
                      Trajeto {index + 1}: {routeItem.summary}
                    </div>
                    <div className="route-card-duration">
                      Tempo estimado: {routeItem.durationText}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="actions-row">
            <button className="btn-back" onClick={handleBack} type="button">
              Voltar
            </button>

            <button className="btn-next" onClick={handleNext} type="button">
              Próximo
            </button>
          </div>
        </div>

        <div className="map-side">
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={{
                width: "100%",
                height: "100%",
              }}
              mapContainerClassName="map-container"
              center={mapCenter || defaultCenter}
              zoom={10}
              options={{ mapId }}
              onLoad={(map) => {
                mapRef.current = map;
                setTimeout(() => {
                  window.google.maps.event.trigger(map, "resize");
                }, 300);
              }}
            >
              {directions && routeOptions.length > 0 && (
                <DirectionsRenderer
                  directions={directions}
                  options={{
                    routeIndex: Math.min(
                      Math.max(selectedRouteIndex, 0),
                      routeOptions.length - 1,
                    ),
                    suppressMarkers: true,
                    polylineOptions: {
                      strokeColor: "#1e88e5",
                      strokeWeight: 4,
                      strokeOpacity: 0.8,
                    },
                  }}
                />
              )}
            </GoogleMap>
          )}
        </div>
      </div>
      <br />
      <StepFooter />
    </>
  );
}

export default Trajeto;
